/**
 * Release 33: the browser's own install offer, held for Pasang SATU. Chrome and Edge fire
 * `beforeinstallprompt` once the page qualifies, early (often before React has mounted), so
 * main.tsx calls `tangkapTawaran` first thing: the event is kept (its default banner held back)
 * until Pasang calls `prompt()`. `appinstalled` makes the device `terpasang` at once and asks
 * again for persistent storage, which Chrome grants an installed app more readily; so does a
 * launch from the icon (db.ts asks at every start as well). The answer lands in
 * `ui().setPersisted`, as db.ts's own.
 */
import { create } from 'zustand'
import { type PenyimpananAlamat, penyimpananPeramban } from '../alamat'
import { useUi } from '../store/ui'
import { type FaktaPeramban, MODE_TERPASANG, type SituasiPasang, situasiPasang } from './situasi'

/** The event Chrome and Edge fire (not in TypeScript's DOM types). */
export interface TawaranPasang extends Event {
  prompt(): Promise<void>
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform?: string }>
}

interface KeadaanPasang {
  /** The held `beforeinstallprompt`; null before it fires and once it has been used. */
  tawaran: TawaranPasang | null
  /** `appinstalled` fired during this visit. */
  baruTerpasang: boolean
}

export const usePasang = create<KeadaanPasang>(() => ({ tawaran: null, baruTerpasang: false }))

/** What the situation is decided from, read from the page (./situasi is pure). */
export function faktaPeramban(w: Window = window): FaktaPeramban {
  const { tawaran, baruTerpasang } = usePasang.getState()
  const nav = w.navigator as Navigator & { standalone?: boolean }
  return {
    userAgent: nav.userAgent,
    maxTouchPoints: nav.maxTouchPoints ?? 0,
    standalone: nav.standalone,
    tampilan: (q) => (typeof w.matchMedia === 'function' ? w.matchMedia(q).matches : false),
    adaPrompt: tawaran !== null,
    baruTerpasang,
  }
}

/** What Pasang SATU needs of the page; the DOM tests give their own device. */
export interface LingkunganPasang {
  hostname: string
  storage: PenyimpananAlamat | null
  /** Epoch ms. */
  sekarang(): number
  /** The device's facts; the held offer and the install are the store's (`useSituasiPasang`). */
  fakta(): FaktaPeramban
}

export function lingkunganPasangPeramban(): LingkunganPasang {
  return {
    hostname: window.location.hostname,
    storage: penyimpananPeramban(),
    sekarang: () => Date.now(),
    fakta: () => faktaPeramban(window),
  }
}

/** Asks for persistent storage; the answer goes where db.ts puts its own. */
export function mintaPenyimpananTetap(nav: Navigator = navigator): void {
  nav.storage
    ?.persist?.()
    .then((granted) => useUi.getState().setPersisted(granted))
    .catch(() => {})
}

/** The windows heard, each with what stops hearing it (the tests start afresh). */
const tertangkap = new Map<Window, () => void>()

/** Holds the browser's offer and hears the install; once per window. */
export function tangkapTawaran(w: Window = window): void {
  if (tertangkap.has(w)) return
  const tawar = (event: Event) => {
    // The browser's own mini-bar would otherwise come first: SATU offers it in its own words.
    event.preventDefault()
    usePasang.setState({ tawaran: event as TawaranPasang })
  }
  const terpasang = () => {
    usePasang.setState({ tawaran: null, baruTerpasang: true })
    mintaPenyimpananTetap(w.navigator)
  }
  w.addEventListener('beforeinstallprompt', tawar)
  w.addEventListener('appinstalled', terpasang)
  tertangkap.set(w, () => {
    w.removeEventListener('beforeinstallprompt', tawar)
    w.removeEventListener('appinstalled', terpasang)
  })
  // Opened from its icon: installed now, so the storage request may be granted where it was not.
  const nav = w.navigator as Navigator & { standalone?: boolean }
  const dariIkon =
    nav.standalone === true ||
    (typeof w.matchMedia === 'function' && MODE_TERPASANG.some((q) => w.matchMedia(q).matches))
  if (dariIkon) mintaPenyimpananTetap(w.navigator)
}

/** For the tests: stop hearing every window and forget the held offer. */
export function aturUlangTawaran(): void {
  for (const lepas of tertangkap.values()) lepas()
  tertangkap.clear()
  usePasang.setState({ tawaran: null, baruTerpasang: false })
}

export type HasilPasang = 'diterima' | 'dibatalkan' | 'tidak-ada'

/** Opens the browser's install dialog with the held offer; the offer is used once. */
export async function pasangSekarang(): Promise<HasilPasang> {
  const tawaran = usePasang.getState().tawaran
  if (tawaran === null) return 'tidak-ada'
  usePasang.setState({ tawaran: null })
  try {
    await tawaran.prompt()
    const { outcome } = await tawaran.userChoice
    return outcome === 'accepted' ? 'diterima' : 'dibatalkan'
  } catch {
    return 'dibatalkan'
  }
}

/**
 * The device's situation, again whenever the offer or the install changes. `fakta` replaces the
 * page's facts (the DOM tests give their own device).
 */
export function useSituasiPasang(fakta?: () => FaktaPeramban): SituasiPasang {
  const tawaran = usePasang((s) => s.tawaran)
  const baruTerpasang = usePasang((s) => s.baruTerpasang)
  // The display mode does not change while a page lives (an install opens a new window).
  const dasar = fakta === undefined ? faktaPeramban() : fakta()
  return situasiPasang({
    ...dasar,
    adaPrompt: tawaran !== null,
    baruTerpasang: baruTerpasang || dasar.baruTerpasang === true,
  })
}
